import { TUNABLES } from "../config";
import { phrases } from "../i18n";
import type { LobbyClient, PublicGame } from "../net/protocol";
import { gamesOnPageClock } from "../net/roomClient";
import { backoffMs, type SocketLike, wireSocket } from "../net/socketLink";
import type { LobbyGame, LobbySource } from "./lobby";

/**
 * The lobby server's duels on the front page (B3.5): the list the lobby object sends, read against
 * the page's clock, so two pages show the same countdown. Until the list comes, and whenever the
 * socket fails or closes, the page shows its fallback (the local duels that start when joined,
 * D-037): a playtest page with no server keeps working. Since B3.7 a socket that fails, closes or
 * does not answer in `connectTimeoutMs` is tried again after `lobbyRetryMs`, so the server's
 * list comes back by itself when the server does.
 */
export interface ServerLobby extends LobbySource {
  /** The server's list is on screen: its duels are joined through their rooms. */
  readonly live: boolean;
  /**
   * D-120: the first try is over, its list came or it failed or fell silent; from then on, true.
   * The front page holds its maps and duels unseen until then.
   */
  readonly settled: boolean;
  close(): void;
}

export function createServerLobby(options: {
  /** A new socket to the lobby, for the first try and every retry. */
  readonly open: () => SocketLike;
  readonly hello: LobbyClient;
  readonly fallback: LobbySource;
  readonly now: () => number;
  /** Runs `task` once after `ms`; the page's `setTimeout`, a test's hand. */
  readonly after: (ms: number, task: () => void) => void;
}): ServerLobby {
  const { fallback } = options;
  let games: readonly PublicGame[] | undefined;
  let socket: SocketLike | undefined;
  let closed = false;
  let attempt = 0;
  let settled = false;

  /** This socket is done: the local duels until the next try. */
  const fail = (from: SocketLike) => {
    if (from !== socket) {
      return;
    }
    settled = true;
    socket = undefined;
    games = undefined;
    if (closed) {
      return;
    }
    const wait = backoffMs(TUNABLES.lobby.lobbyRetryMs, attempt);
    attempt += 1;
    options.after(wait, connect);
  };

  function connect(): void {
    if (closed) {
      return;
    }
    const current = options.open();
    socket = current;
    wireSocket(current, {
      open: () => current.send(JSON.stringify(options.hello)),
      message: (text) => {
        const list =
          text !== undefined && current === socket
            ? gamesOnPageClock(text, options.now())
            : undefined;
        if (list) {
          games = list;
          attempt = 0;
          settled = true;
        }
      },
      gone: () => fail(current),
    });
    options.after(TUNABLES.lobby.connectTimeoutMs, () => {
      if (current === socket && games === undefined) {
        current.close();
        fail(current);
      }
    });
  }

  connect();
  return {
    get live() {
      return games !== undefined;
    },
    get settled() {
      return settled;
    },
    get kind() {
      return games === undefined ? fallback.kind : "server";
    },
    get title() {
      return games === undefined ? fallback.title : phrases().front.lobby.duelsSoon;
    },
    games(now): readonly LobbyGame[] {
      return games ?? fallback.games(now);
    },
    close() {
      closed = true;
      games = undefined;
      socket?.close();
      socket = undefined;
    },
  };
}

/**
 * The lobby server's address for this page, or undefined for none: the build's `VITE_LOBBY_URL`
 * (empty until B3.8), and in dev and e2e builds `?lobby=server` reaches `wrangler dev`.
 * `?lobby=local` keeps the local duels whatever the build says.
 */
export function lobbyServerUrl(
  search: string,
  built: string | undefined,
  devBuild: boolean,
): string | undefined {
  const asked = new URLSearchParams(search).get("lobby");
  if (asked === "local") {
    return undefined;
  }
  if (devBuild && asked === "server") {
    // B3.7: `&lobbyUrl=` aims a dev page at another server, as the kill switch's test does.
    return new URLSearchParams(search).get("lobbyUrl") || built || TUNABLES.lobby.devServerUrl;
  }
  return built || undefined;
}
